import { Link } from 'react-router-dom';
import BlogPostLayout from '../components/BlogPostLayout';
import type { ContentsItem } from '../components/TableOfContents';
import { Helmet } from "react-helmet-async";

const FUNDING_CARE_CONTENTS: ContentsItem[] = [
  { level: 'h2', text: 'Quick answer: How will you pay?', id: 'quick-answer' },
  { level: 'h2', text: 'Quick guide: Your funding options', id: 'funding-options' },
  { level: 'h3', text: 'Self-funding (assets over £23,250)', id: 'self-funding' },
  { level: 'h3', text: 'Local authority funding (assets below £23,250)', id: 'local-authority-funding' },
  { level: 'h3', text: 'NHS Continuing Healthcare (complex health needs)', id: 'nhs-chc' },
  { level: 'h2', text: 'Understanding the £23,250 threshold', id: 'threshold' },
  { level: 'h2', text: 'Self-funding in detail', id: 'self-funding-detail' },
  { level: 'h3', text: 'Managing self-funding', id: 'managing-self-funding' },
  { level: 'h3', text: 'When to apply for local authority funding', id: 'when-to-apply' },
  { level: 'h3', text: 'Protecting your property', id: 'protecting-property' },
  { level: 'h2', text: 'Local authority funding in detail', id: 'local-authority-detail' },
  { level: 'h3', text: 'The financial assessment', id: 'financial-assessment' },
  { level: 'h3', text: 'Council funding rates', id: 'council-rates' },
  { level: 'h3', text: 'Choice and availability', id: 'choice-availability' },
  { level: 'h3', text: 'Reviews and reassessments', id: 'reviews-reassessments' },
  { level: 'h2', text: 'NHS Continuing Healthcare in detail', id: 'nhs-chc-detail' },
  { level: 'h3', text: 'Who qualifies?', id: 'who-qualifies' },
  { level: 'h3', text: 'The CHC assessment process', id: 'chc-assessment' },
  { level: 'h3', text: 'Challenging CHC decisions', id: 'challenging-chc' },
  { level: 'h3', text: 'CHC and care homes in Whitstable', id: 'chc-care-homes' },
  { level: 'h2', text: 'Other funding options', id: 'other-funding' },
  { level: 'h2', text: 'Financial planning and advice', id: 'financial-planning' },
  { level: 'h3', text: 'Types of advisors', id: 'types-advisors' },
  { level: 'h2', text: 'Planning ahead for care costs', id: 'planning-ahead' },
  { level: 'h3', text: 'Estate planning', id: 'estate-planning' },
  { level: 'h3', text: 'Powers of Attorney', id: 'powers-attorney' },
  { level: 'h2', text: 'Cost comparisons: Different funding routes', id: 'cost-comparisons' },
  { level: 'h2', text: 'Common funding questions', id: 'common-questions' },
  { level: 'h2', text: 'Taking the next step', id: 'next-step' },
  { level: 'h2', text: 'How Harpwood House can help', id: 'harpwood-house-help' },
  { level: 'h2', text: 'Resources and contacts', id: 'resources-contacts' },
  { level: 'h2', text: 'Get personalised funding advice', id: 'personalised-advice' },
];

function FundingCareHomeFees() {
  return (
    <>
    <Helmet>
        <title> Funding Care Home Fees in Whitstable </title>
        <meta
          name="description"
          content="Our guide explains self-funding, local authority support, and NHS contributions to help you understand how care home fees are paid in the UK."
        />
        <link rel="canonical" href="https://www.harpwood.house/help-and-advice/post/funding-care-home-fees-whitstable" />
      </Helmet>

    <BlogPostLayout title="Funding Care Home Fees in Whitstable" contents={FUNDING_CARE_CONTENTS}>
      <div className="nursing-video-wrapper" style={{ marginTop: 0, marginBottom: 'var(--space-6)' }}>
        <iframe
          src="https://player.vimeo.com/video/1168105398?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          title="Graham Care - January - Cost Of Fees SEO"
        />
      </div>
      <div className="blog-post-content">
        <h2 id="quick-answer">Quick answer: How will you pay?</h2>
        <p>Three main funding routes for care homes in Whitstable:</p>
        <ul>
          <li><strong>Self-funding</strong> – If your assets (including property) exceed £23,250</li>
          <li><strong>Local authority funding</strong> – If your assets are below £23,250 following a care needs assessment</li>
          <li><strong>NHS Continuing Healthcare</strong> – Fully funded by NHS for complex health needs (not means-tested)</li>
        </ul>
        <p><strong>Average weekly costs in Whitstable: £1,234 to £2,000</strong></p>
        <p>Most people self-fund initially. Around 40% eventually receive some local authority support. Only about 20% qualify for NHS Continuing Healthcare.</p>

        <h2 id="funding-options">Quick guide: Your funding options</h2>

        <h3 id="self-funding">Self-funding (assets over £23,250)</h3>
        <p>You pay the full cost of care from savings, pensions, property, or other assets.</p>
        <p><strong>Advantages:</strong></p>
        <ul>
          <li>Full choice of care home</li>
          <li>No waiting for assessments</li>
          <li>Control over decisions</li>
          <li>Potentially higher-quality care options</li>
        </ul>
        <p><strong>Key points:</strong></p>
        <ul>
          <li>Must pay until assets fall below £23,250</li>
          <li>Can then apply for local authority support</li>
          <li>Property is counted if unoccupied</li>
          <li>Spouse/partner living in property is exempt</li>
        </ul>
        <p><Link to="/self-funding" className="nurse-care-link">Learn more about self-funding →</Link></p>

        <h3 id="local-authority-funding">Local authority funding (assets below £23,250)</h3>
        <p>Canterbury City Council funds or contributes towards care after a care needs assessment.</p>
        <p><strong>How it works:</strong></p>
        <ul>
          <li>Request a care needs assessment</li>
          <li>Financial assessment determines contribution</li>
          <li>Council sets a weekly rate they'll pay</li>
          <li>Limited choice to contracted homes</li>
          <li>May need to accept first available placement</li>
        </ul>
        <p><strong>Partial contributions:</strong></p>
        <ul>
          <li>Assets £14,250–£23,250: You contribute from assets</li>
          <li>Income over personal allowance: You contribute from income</li>
          <li>Council tops up the remainder</li>
        </ul>
        <p><Link to="/social-services-funding" className="nurse-care-link">Learn more about social services funding →</Link></p>

        <h3 id="nhs-chc">NHS Continuing Healthcare (complex health needs)</h3>
        <p>Full NHS funding for people whose primary need is healthcare rather than social care.</p>
        <p><strong>Eligibility based on:</strong></p>
        <ul>
          <li>Nature, complexity, intensity, and unpredictability of health needs</li>
          <li>Not specific diagnoses or conditions</li>
          <li>Assessment using NHS framework</li>
          <li>Fast-track available for terminal illness</li>
        </ul>
        <p><strong>Benefits:</strong></p>
        <ul>
          <li>Completely free (no means testing)</li>
          <li>Covers all care costs</li>
          <li>Wide choice of homes</li>
          <li>Applies regardless of your finances</li>
        </ul>
        <p><Link to="/nhs-funding" className="nurse-care-link">Learn more about NHS funding →</Link></p>

        <h2 id="threshold">Understanding the £23,250 threshold</h2>
        <p>This critical figure is the dividing line in England's care funding system, determining whether you'll need to pay for your own care or qualify for local authority support. It's the total value of your savings, investments, and property (with specific exceptions) that decides which funding route applies to you.</p>

        <p><strong>What counts towards the £23,250:</strong></p>
        <ul>
          <li>Savings accounts and investments</li>
          <li>Stocks, shares, and bonds</li>
          <li>Property (with important exceptions)</li>
          <li>Income above personal allowance</li>
          <li>Valuable possessions (cars, jewellery if substantial)</li>
        </ul>

        <p><strong>What doesn't count:</strong></p>
        <ul>
          <li>Property if your spouse/partner still lives there</li>
          <li>Property if a close relative over 60 lives there</li>
          <li>Property if a disabled relative lives there</li>
          <li>Personal possessions (furniture, clothing)</li>
          <li>Surrender value of life insurance policies</li>
          <li>Personal injury compensation payments</li>
        </ul>

        <p><strong>The capital tiers:</strong></p>
        <ul>
          <li><strong>Over £23,250</strong> – Full self-funding, no local authority support</li>
          <li><strong>£14,250 to £23,250</strong> – Partial contribution required. For every £250 over £14,250, you contribute an additional £1 per week. Council funds the remainder.</li>
          <li><strong>Under £14,250</strong> – No contribution from capital, but you may contribute from income above your personal allowance (currently £28.25 per week).</li>
        </ul>

        <h2 id="self-funding-detail">Self-funding in detail</h2>
        <p>Self-funding, also known as private funding, is the most common initial route into care homes because most property-owning families have assets exceeding the £23,250 threshold. Understanding how to manage this effectively can help your money last longer and ensure you transition smoothly to local authority funding when the time comes.</p>

        <h3 id="managing-self-funding">Managing self-funding</h3>
        <p><strong>Payment options:</strong></p>
        <ul>
          <li>Direct payments to the care home monthly or weekly</li>
          <li>Using income from pensions and investments</li>
          <li>Gradually depleting savings</li>
          <li>Selling property to fund care</li>
          <li>Equity release or property rental</li>
          <li>Deferred payment agreements (see below)</li>
        </ul>

        <p><strong>Financial planning considerations:</strong></p>
        <ul>
          <li>Plan how long your funds will last. At £1,300 per week (average Whitstable cost), annual costs are approximately £67,600.</li>
          <li>£100,000 in savings = approximately 18 months of care</li>
          <li>£200,000 = approximately 3 years</li>
          <li>£300,000 = approximately 4.5 years</li>
          <li>Factor in fee increases (typically 3–5% annually) and declining asset values as you spend them.</li>
        </ul>

        <h3 id="when-to-apply">When to apply for local authority funding</h3>
        <p>Once your assets fall to around £30,000, start the local authority assessment process. Assessments take time, and you want funding in place before reaching the £23,250 threshold.</p>
        <p>Don't wait until you've completely depleted savings, as gaps in funding can cause stress and potential placement disruption.</p>

        <h3 id="protecting-property">Protecting your property</h3>
        <p><strong>If your spouse/partner lives there:</strong> Your property is disregarded completely. They can continue living there indefinitely without it affecting your care funding.</p>
        <p><strong>If the property is empty:</strong> After 12 weeks, the property is included in your assets and you're expected to use its value to fund care. Options include:</p>
        <ul>
          <li>Selling the property and using proceeds</li>
          <li>Renting it out for income</li>
          <li>Deferred payment agreement (council loan secured against property)</li>
          <li>Family members purchasing the property</li>
          <li>Equity release schemes</li>
        </ul>

        <p><strong>Property protection strategies:</strong></p>
        <p>Transferring property to avoid care fees is called "deprivation of assets" and is illegal. Councils will investigate and can reverse transactions. However, legitimate estate planning done years before needing care is different from last-minute transfers.</p>
        <p>Seek professional legal advice if considering property arrangements before care needs arise.</p>

        <h2 id="local-authority-detail">Local authority funding in detail</h2>
        <p>Canterbury City Council provides care funding through a structured assessment process that evaluates both your care needs and financial circumstances. This route involves more bureaucracy and less choice than self-funding, but provides essential support once your assets have depleted below the threshold, ensuring you continue receiving the care you need.</p>

        <p><strong>The assessment considers:</strong></p>
        <ul>
          <li>Physical health and mobility</li>
          <li>Mental health and cognition</li>
          <li>Personal care requirements</li>
          <li>Medication needs</li>
          <li>Safety and risk</li>
          <li>Social needs and relationships</li>
          <li>Family support available</li>
        </ul>

        <p><strong>Outcomes:</strong></p>
        <ul>
          <li>Eligible for local authority support</li>
          <li>Signposted to other services</li>
          <li>Not eligible but may need reassessment later</li>
        </ul>

        <h3 id="financial-assessment">The financial assessment</h3>
        <p>If eligible for care, you'll have a financial assessment to determine contributions.</p>
        <p><strong>Information required:</strong></p>
        <ul>
          <li>Bank statements and savings accounts</li>
          <li>Property ownership and value</li>
          <li>Income (pensions, benefits, investments)</li>
          <li>Assets and valuables</li>
          <li>Regular expenses</li>
        </ul>

        <p><strong>Your contribution:</strong></p>
        <ul>
          <li>Calculated based on assets and income</li>
          <li>Personal allowance of £28.25 per week protected</li>
          <li>Council funds any shortfall up to their rate</li>
        </ul>

        <h3 id="council-rates">Council funding rates</h3>
        <p>Canterbury City Council sets maximum weekly rates they'll pay for care. These rates are typically lower than average care home fees in Whitstable.</p>
        <p>This means: If the council rate is £900 per week but your chosen home charges £1,300, there's a £400 weekly shortfall. Options include:</p>
        <ul>
          <li>Choose a home within the council rate</li>
          <li>Family pays "top-up" fees (the difference)</li>
          <li>Use personal funds if you have any</li>
          <li>Negotiate with the care home</li>
        </ul>

        <h3 id="choice-availability">Choice and availability</h3>
        <p>Local authority funding limits your choice to homes contracted by Canterbury City Council. Popular homes may have waiting lists for council-funded places.</p>
        <p>You have the right to choose from available contracted homes, but cannot insist on a specific home if no council-funded places are available there.</p>

        <h3 id="reviews-reassessments">Reviews and reassessments</h3>
        <p>Local authority funding requires regular reviews:</p>
        <ul>
          <li>Care needs reviewed annually (minimum)</li>
          <li>Financial circumstances reviewed annually</li>
          <li>More frequent reviews if circumstances change</li>
          <li>Must report changes in income or assets</li>
        </ul>
        <p><Link to="/social-services-funding" className="nurse-care-link">Learn more about social services funding at Harpwood House →</Link></p>

        <h2 id="nhs-chc-detail">NHS Continuing Healthcare in detail</h2>
        <p>NHS Continuing Healthcare (CHC) is often misunderstood but represents the most comprehensive funding option available—completely free care regardless of your financial situation. The key is understanding what "primary health need" means and ensuring your assessment accurately reflects the complexity and intensity of your healthcare requirements.</p>

        <p><strong>Key points:</strong></p>
        <ul>
          <li>Covers all care costs (accommodation, care, and nursing)</li>
          <li>Available in care homes or your own home</li>
          <li>Based on health needs, not specific diagnoses</li>
          <li>Completely free if eligible</li>
          <li>Subject to regular review</li>
        </ul>

        <h3 id="who-qualifies">Who qualifies?</h3>
        <p>Eligibility depends on having a "primary health need" – where your main requirement is healthcare rather than social care.</p>
        <p><strong>The assessment considers:</strong></p>
        <ul>
          <li><strong>Nature</strong> of your needs (type and quality of care required)</li>
          <li><strong>Complexity</strong> (how your needs interact and impact each other)</li>
          <li><strong>Intensity</strong> (extent and frequency of care needed)</li>
          <li><strong>Unpredictability</strong> (how your condition varies and risks involved)</li>
        </ul>

        <h3 id="chc-assessment">The CHC assessment process</h3>
        <p><strong>1. Initial checklist</strong></p>
        <p>A nurse or social worker completes a CHC checklist to determine if you should proceed to full assessment.</p>

        <p><strong>2. Full assessment</strong></p>
        <p>If the checklist is positive, a multidisciplinary team completes the Decision Support Tool (DST), gathering evidence from: doctors and consultants, nurses, social workers, therapists, family members, care staff.</p>

        <p><strong>3. Decision</strong></p>
        <p>Your local Integrated Care Board (ICB) reviews the assessment and decides eligibility. You should receive written notification within 28 days.</p>

        <p><strong>4. Fast-track pathway</strong></p>
        <p>If you're terminally ill or rapidly deteriorating, a fast-track tool allows immediate CHC funding without the full assessment. Care should be arranged within 48 hours.</p>

        <h3 id="challenging-chc">Challenging CHC decisions</h3>
        <p>Around 80% of CHC applications are rejected initially. If you believe the decision is wrong:</p>
        <ol>
          <li>Request a local resolution with your ICB within 6 months</li>
          <li>Request an independent review through NHS England if local resolution fails</li>
          <li>Contact the Parliamentary and Health Service Ombudsman as a final step</li>
        </ol>
        <p>Many successful CHC awards happen after appeals, so don't be discouraged by initial rejection if you believe you meet the criteria.</p>

        <h3 id="chc-care-homes">CHC and care homes in Whitstable</h3>
        <p>If awarded CHC, you can choose any care home in Whitstable that meets your needs, regardless of cost (within reasonable limits). The NHS pays all fees directly to the home.</p>
        <p><Link to="/nhs-funding" className="nurse-care-link">Learn more about NHS funding at Harpwood House →</Link></p>

        <h2 id="other-funding">Other funding options</h2>
        <p>Beyond the three main funding routes, several additional financial supports and benefits exist that can help reduce the overall cost burden of care. These supplementary funding sources won't typically cover full care home fees, but they can make a meaningful contribution, particularly for those self-funding or paying top-up fees to local authority funding.</p>
        <p><strong>Attendance Allowance</strong> – This is paid to you regardless of savings or income and can contribute towards care home fees if you're self-funding or topping up council funding.</p>
        <p>Apply at <a href="https://www.gov.uk/attendance-allowance" target="_blank" rel="noopener noreferrer">www.gov.uk/attendance-allowance</a></p>
        <p>Other options include Veterans' support, charitable support or family contributions.</p>

        <h2 id="financial-planning">Financial planning and advice</h2>
        <p>Navigating care funding is complex, with significant financial implications for both you and your family. Professional advice tailored to your specific circumstances can help you make informed decisions, avoid costly mistakes, and ensure you access all the funding support available to you—potentially saving thousands of pounds over the course of care.</p>

        <p><strong>Seek advice if you:</strong></p>
        <ul>
          <li>Own property and need care</li>
          <li>Have significant savings or investments</li>
          <li>Want to plan ahead for potential care needs</li>
          <li>Are considering equity release or property sales</li>
          <li>Need to understand tax implications</li>
          <li>Want to protect assets for beneficiaries (legitimately)</li>
        </ul>

        <h3 id="types-advisors">Types of advisors</h3>
        <ul>
          <li><strong>Independent Financial Advisors (IFAs):</strong> Provide advice on funding care, investments, and long-term planning. Choose specialists in later-life planning.</li>
          <li><strong>Solicitors:</strong> Essential for legal matters including powers of attorney, property arrangements, and estate planning.</li>
          <li><strong>Care funding specialists:</strong> Focus specifically on navigating care funding systems and maximising available support.</li>
          <li><strong>Local authority advisors:</strong> Free guidance from Canterbury City Council's adult social care team on assessments and funding processes.</li>
        </ul>

        <h2 id="planning-ahead">Planning ahead for care costs</h2>
        <p>The earlier you start thinking about potential care costs, the more control you have over your financial future and the better equipped you'll be to make calm, informed decisions. Planning ahead isn't about pessimism—it's about ensuring that if care becomes necessary, you can focus on wellbeing rather than scrambling to understand complex funding systems under pressure.</p>

        <p><strong>Benefits:</strong></p>
        <ul>
          <li>Time to arrange finances optimally</li>
          <li>Legitimate estate planning possible</li>
          <li>Less stress when care is needed</li>
          <li>Better decision-making without pressure</li>
          <li>Understanding what you can afford</li>
        </ul>

        <h3 id="estate-planning">Estate planning</h3>
        <p><strong>If you're concerned about care costs affecting your estate:</strong></p>
        <p><strong>Legitimate approaches:</strong></p>
        <ul>
          <li>Spending assets during your lifetime on yourself</li>
          <li>Gifting within inheritance tax allowances (years before care needs)</li>
          <li>Setting up trusts (with proper legal advice, well in advance)</li>
          <li>Life insurance policies to benefit beneficiaries</li>
          <li>Downsizing to release equity for living costs</li>
        </ul>
        <p><strong>Avoid:</strong></p>
        <ul>
          <li>Last-minute property transfers</li>
          <li>Artificially creating trusts when care is imminent</li>
          <li>Hiding assets or providing false information</li>
          <li>Deliberate deprivation of assets</li>
        </ul>

        <h3 id="powers-attorney">Powers of Attorney</h3>
        <p>Arrange Lasting Powers of Attorney while you have mental capacity:</p>
        <ul>
          <li><strong>Property and Financial Affairs LPA:</strong> Allows someone to manage your money and property if you lose capacity.</li>
          <li><strong>Health and Welfare LPA:</strong> Allows decisions about your care and treatment if you can't decide yourself.</li>
        </ul>
        <p>These cost £82 each and are essential for managing care funding if you lose capacity through dementia or other conditions.</p>

        <h2 id="cost-comparisons">Cost comparisons: Different funding routes</h2>
        <p>Understanding how the same care home fees are covered under different funding routes helps illustrate the practical implications of each approach. These examples use realistic Whitstable costs to show what you'd actually pay depending on your financial circumstances and eligibility for support.</p>

        <p><strong>Self-funding with £250,000 in assets:</strong></p>
        <ul>
          <li>Pay full £1,300 per week = £67,600 per year</li>
          <li>Assets last approximately 3.5 years</li>
          <li>Then apply for local authority funding</li>
        </ul>

        <p><strong>Local authority funding with £20,000 in assets:</strong></p>
        <ul>
          <li>Council contribution: £900 per week</li>
          <li>Your contribution from assets: £240 per week</li>
          <li>Family top-up required: £400 per week = £20,800 per year – or choose a home within council rate</li>
        </ul>

        <p><strong>NHS Continuing Healthcare:</strong></p>
        <ul>
          <li>NHS pays full £1,300 per week</li>
          <li>No cost to you or family</li>
          <li>Your assets are irrelevant</li>
        </ul>

        <h2 id="common-questions">Common funding questions</h2>
        <p>Certain questions come up repeatedly when families are navigating care funding. Here are honest, straightforward answers:</p>

        <p><strong>What if I run out of money?</strong></p>
        <p>Once assets fall below £23,250, apply for local authority funding. The council must ensure you receive appropriate care, though your choice of home may be limited. Never let fear of running out of money prevent you from getting needed care. Protections exist to ensure basic care is provided.</p>

        <p><strong>Can my spouse be forced to sell our home?</strong></p>
        <p>No. If your spouse or partner lives in your shared home, it's completely disregarded for care funding purposes. They can continue living there indefinitely.</p>

        <p><strong>What happens to my home when I die?</strong></p>
        <p>If you've self-funded care, your property passes through your estate according to your will. If you have a deferred payment agreement, the debt is repaid from estate funds (usually by selling the property).</p>

        <p><strong>Do care home fees ever go down?</strong></p>
        <p>No. Fees typically increase annually by 3–5% or more. Budget for regular increases when planning long-term funding.</p>

        <p><strong>Can I move care homes if funded by the council?</strong></p>
        <p>Yes, but your choice is limited to homes contracted by the council with available places. Moving between self-funded homes is easier.</p>

        <h2 id="next-step">Taking the next step</h2>
        <p>Understanding funding options is just the beginning; the real value comes from taking action based on your specific situation.</p>

        <p><strong>1. Assess your situation</strong></p>
        <ul>
          <li>Calculate total assets including property</li>
          <li>Understand your income</li>
          <li>Determine which funding route applies</li>
        </ul>

        <p><strong>2. Request care needs assessment</strong></p>
        <ul>
          <li>Contact Canterbury City Council if potentially eligible for support</li>
          <li>Don't wait until crisis point</li>
          <li>Assessment takes weeks, so start early</li>
        </ul>

        <p><strong>3. Explore NHS Continuing Healthcare</strong></p>
        <ul>
          <li>If you have complex health needs, request CHC assessment</li>
          <li>Don't assume you won't qualify</li>
          <li>Many successful awards happen after initial rejection</li>
        </ul>

        <p><strong>4. Get professional advice</strong></p>
        <ul>
          <li>Speak to financial advisors about complex situations</li>
          <li>Arrange legal advice for property and powers of attorney</li>
          <li>Use free council advice services</li>
        </ul>

        <p><strong>5. Research care homes</strong></p>
        <ul>
          <li>Understand costs in Whitstable</li>
          <li>Visit homes that accept your funding type</li>
          <li>Ask about fee structures and increases</li>
        </ul>

        <h2 id="harpwood-house-help">How Harpwood House can help</h2>
        <p>At Harpwood House, we support families navigating all funding routes:</p>
        <ul>
          <li>We accept self-funding residents</li>
          <li>We work with Canterbury City Council for local authority placements</li>
          <li>We assist with NHS Continuing Healthcare applications</li>
          <li>We provide transparent fee information</li>
          <li>Our team explains funding options clearly</li>
        </ul>
        <p><Link to="/contact-us" className="nurse-care-link">Visit Harpwood House to learn more →</Link></p>

        <h2 id="resources-contacts">Resources and contacts</h2>
        <ul>
          <li><strong>Canterbury City Council</strong> – For care needs assessments and local authority funding. Website: <a href="https://www.canterbury.gov.uk" target="_blank" rel="noopener noreferrer">www.canterbury.gov.uk</a> | Phone: 03000 41 61 61</li>
          <li><strong>NHS Continuing Healthcare</strong> – For CHC assessments and appeals. Website: <a href="https://www.nhs.uk/chc" target="_blank" rel="noopener noreferrer">www.nhs.uk/chc</a></li>
          <li><strong>Attendance Allowance</strong> – For benefit applications. Website: <a href="https://www.gov.uk/attendance-allowance" target="_blank" rel="noopener noreferrer">www.gov.uk/attendance-allowance</a> | Phone: 0800 731 0122</li>
          <li><strong>Age UK</strong> – Free advice on care and funding. Website: <a href="https://www.ageuk.org.uk" target="_blank" rel="noopener noreferrer">www.ageuk.org.uk</a> | Phone: 0800 678 1602</li>
          <li><strong>Independent Age</strong> – Advice for older people. Website: <a href="https://www.independentage.org" target="_blank" rel="noopener noreferrer">www.independentage.org</a> | Phone: 0800 319 6789</li>
          <li><strong>Citizens Advice</strong> – General advice and guidance. Website: <a href="https://www.citizensadvice.org.uk" target="_blank" rel="noopener noreferrer">www.citizensadvice.org.uk</a></li>
          <li><strong>Care Quality Commission</strong> – Check care home ratings. Website: <a href="https://www.cqc.org.uk" target="_blank" rel="noopener noreferrer">www.cqc.org.uk</a></li>
        </ul>

        <h2 id="personalised-advice">Get personalised funding advice</h2>
        <p>Every family's financial situation is different. At Harpwood House, we understand the intricacies of care funding and can provide personalised advice based on your specific circumstances.</p>
        <p><Link to="/contact-us" className="nurse-care-link">Make an enquiry →</Link></p>
        <p>Email: <a href="mailto:enquiries@harpwood.house">enquiries@harpwood.house</a></p>
        <p>Phone: <a href="tel:01227533522">01227 533522</a></p>
        <p>Our team understands the complexities of care funding and can guide you through your options, whether you're self-funding, applying for local authority support, or pursuing NHS Continuing Healthcare.</p>
      </div>
    </BlogPostLayout>
    </>
  );
}

export default FundingCareHomeFees;
